import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useAppDispatch } from "../../hooks/useStore";
import { useSuperAdminAuth } from "../../hooks/useSuperAdminAuth";
import {
  updateSuperAdminProfile,
  getSuperAdminProfile,
} from "../../store/slices/superAdminAuthSlice";
import { withVersion } from "../../utils/concurrency";
import { Input, FormField } from "../../components/UI/FormField";
import { SectionCard } from "../../components/UI/SectionCard";
import Button from "../../components/UI/Button";
import { User, Lock, Info } from "lucide-react";
import { toast } from "react-toastify";
import type { UpdateSuperAdminProfileData } from "../../types/superadmin";

const profileSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(2, "First name must be at least 2 characters")
    .nullable(),
  lastName: Yup.string()
    .min(2, "Last name must be at least 2 characters")
    .nullable(),
  currentPassword: Yup.string()
    .nullable()
    .when("newPassword", {
      is: (val: string) => val && val.length > 0,
      then: (schema) =>
        schema.required("Current password is required to change password"),
    }),
  newPassword: Yup.string()
    .nullable()
    .test(
      "password-requirements",
      "Password must meet all requirements",
      function (value) {
        if (!value) return true;

        const errors = [];
        if (value.length < 8)
          errors.push("Password must be at least 8 characters");
        if (!/[a-z]/.test(value))
          errors.push("Password must contain at least one lowercase letter");
        if (!/[A-Z]/.test(value))
          errors.push("Password must contain at least one uppercase letter");
        if (!/\d/.test(value))
          errors.push("Password must contain at least one number");
        if (!/[@$!%*?&]/.test(value))
          errors.push("Password must contain at least one special character");

        if (errors.length > 0) {
          return this.createError({ message: errors.join(", ") });
        }
        return true;
      },
    ),
  confirmNewPassword: Yup.string()
    .nullable()
    .when("newPassword", {
      is: (val: string) => val && val.length > 0,
      then: (schema) =>
        schema
          .required("Please confirm your new password")
          .oneOf([Yup.ref("newPassword")], "Passwords must match"),
    }),
});

const SuperAdminProfilePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { superAdmin, loading } = useSuperAdminAuth();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<UpdateSuperAdminProfileData>({
    resolver: yupResolver(profileSchema as any),
    defaultValues: {
      firstName: superAdmin?.firstName || "",
      lastName: superAdmin?.lastName || "",
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  useEffect(() => {
    if (superAdmin) {
      reset({
        firstName: superAdmin.firstName,
        lastName: superAdmin.lastName,
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });
    }
  }, [superAdmin, reset]);

  const onSubmit = async (data: UpdateSuperAdminProfileData) => {
    try {
      const updateData: any = {};
      if (data.firstName) updateData.firstName = data.firstName;
      if (data.lastName) updateData.lastName = data.lastName;
      if (data.currentPassword && data.newPassword) {
        updateData.currentPassword = data.currentPassword;
        updateData.newPassword = data.newPassword;
      }

      await dispatch(
        updateSuperAdminProfile(withVersion(updateData, superAdmin)),
      ).unwrap();
      toast.success("Profile updated successfully");

      await dispatch(getSuperAdminProfile());

      reset({
        ...data,
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });
    } catch (err: any) {
      toast.error(err || "Failed to update profile");
    }
  };

  return (
    <div className="p-3 max-w-3xl mx-auto">
      <div className="mb-3">
        <h1 className="text-lg font-bold text-gray-900">Profile Settings</h1>
        <p className="text-[12px] text-gray-500">
          Manage your SuperAdmin account settings
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <SectionCard icon={User} title="Personal Information">
          <div className="space-y-1">
            <FormField
              layout="inline"
              size="sm"
              label="First Name"
              required
              error={errors.firstName?.message}
            >
              <Input
                {...register("firstName")}
                size="sm"
                placeholder="John"
                error={!!errors.firstName}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label="Last Name"
              required
              error={errors.lastName?.message}
            >
              <Input
                {...register("lastName")}
                size="sm"
                placeholder="Doe"
                error={!!errors.lastName}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label="Email"
              hint="Email cannot be changed"
            >
              <Input
                value={superAdmin?.email || ""}
                readOnly
                size="sm"
                className="bg-gray-50"
              />
            </FormField>
          </div>
        </SectionCard>

        <SectionCard icon={Lock} title="Change Password">
          <div className="space-y-1">
            <FormField
              layout="inline"
              size="sm"
              label="Current Password"
              error={errors.currentPassword?.message}
            >
              <Input
                {...register("currentPassword")}
                type="password"
                size="sm"
                placeholder="••••••••"
                error={!!errors.currentPassword}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label="New Password"
              error={errors.newPassword?.message}
            >
              <Input
                {...register("newPassword")}
                type="password"
                size="sm"
                placeholder="••••••••"
                error={!!errors.newPassword}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label="Confirm New Password"
              error={errors.confirmNewPassword?.message}
            >
              <Input
                {...register("confirmNewPassword")}
                type="password"
                size="sm"
                placeholder="••••••••"
                error={!!errors.confirmNewPassword}
              />
            </FormField>

            <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-md text-[11px] text-blue-800">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <div>
                <strong className="font-semibold">Requirements:</strong>{" "}
                min 8 chars · uppercase · lowercase · number · special (@$!%*?&)
              </div>
            </div>
          </div>
        </SectionCard>

        <div className="flex justify-end items-center gap-2 px-4 py-2.5 border-t border-gray-200 bg-white">
          <Button
            type="button"
            variant="outline"
            onClick={() => reset()}
            disabled={!isDirty || loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!isDirty || loading}
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SuperAdminProfilePage;
